import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useChitChatProfile } from "@/hooks/useChitChatProfile";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { GradientAvatar } from "./GradientAvatar";
import { AfroPattern } from "./AfroPattern";
import {
  Phone, Bell, Shield, Moon, LogOut, Pencil, MessageCircle, Users, Sparkles, ChevronRight,
} from "lucide-react";

export function ChitChatProfilePanel({
  onEditProfile,
  onOpenPrivacy,
  onOpenNotifications,
}: {
  onEditProfile: () => void;
  onOpenPrivacy: () => void;
  onOpenNotifications?: () => void;
}) {
  const { user, signOut } = useAuth();
  const { profile, upsert } = useChitChatProfile();
  const [dark, setDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  const [stats, setStats] = useState({ contacts: 0, groups: 0, stories: 0 });

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: mems } = await supabase
        .from("chitchat_members")
        .select("conversation_id")
        .eq("user_id", user.id);
      const ids = (mems ?? []).map((m: any) => m.conversation_id);
      if (!ids.length) {
        setStats({ contacts: 0, groups: 0, stories: 0 });
        return;
      }
      const { data: convs } = await supabase
        .from("chitchat_conversations")
        .select("id, is_group")
        .in("id", ids);
      const groupIds = (convs ?? []).filter((c: any) => c.is_group).map((c: any) => c.id);
      const directIds = (convs ?? []).filter((c: any) => !c.is_group).map((c: any) => c.id);
      let contacts = 0;
      if (directIds.length) {
        const { data: peers } = await supabase
          .from("chitchat_members")
          .select("user_id")
          .in("conversation_id", directIds)
          .neq("user_id", user.id);
        contacts = new Set((peers ?? []).map((p: any) => p.user_id)).size;
      }
      const { count: stories } = await supabase
        .from("chitchat_statuses")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .gt("expires_at", new Date().toISOString());
      setStats({ contacts, groups: groupIds.length, stories: stories ?? 0 });
    })();
  }, [user?.id]);

  const toggleDark = (v: boolean) => {
    setDark(v);
    document.documentElement.classList.toggle("dark", v);
    upsert({ theme: v ? "dark" : "light" });
  };

  return (
    <div className="pb-6">
      {/* Cover */}
      <div className="relative h-32 chitchat-cover-bg overflow-hidden">
        {/* Gold top accent */}
        <div className="absolute top-0 inset-x-0 h-1 bg-cc-gold" />
        {/* Kente diamond overlay @ 12% */}
        <AfroPattern variant="kente" opacity={0.12} color="#FAF6F1" />
        <Button
          onClick={onEditProfile}
          className="chitchat-btn-gold absolute top-3 right-3 h-8 px-3 text-xs"
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit Profile
        </Button>
      </div>

      {/* Avatar overlap */}
      <div className="px-4 -mt-12 relative">
        <div
          className="rounded-full bg-cc-surface inline-block"
          style={{ padding: 3.5, boxShadow: "var(--cc-shadow-lg)" }}
        >
          <GradientAvatar
            id={user?.id}
            name={profile?.display_name || user?.email || "Me"}
            src={profile?.avatar_url}
            size={88}
          />
        </div>

        {/* Identity */}
        <div className="mt-3">
          <h2 className="font-display text-xl font-bold text-cc-ink">
            {profile?.display_name || "Your name"}
          </h2>
          <p className="text-xs text-cc-ink-soft">
            {profile?.username ? `@${profile.username}` : user?.email}
          </p>
          {profile?.bio && (
            <p className="text-sm text-cc-ink mt-2 max-w-prose">{profile.bio}</p>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mt-4 chitchat-surface p-3">
          <Stat icon={<MessageCircle className="h-4 w-4" />} label="Contacts" value={stats.contacts} />
          <Stat icon={<Users className="h-4 w-4" />} label="Groups" value={stats.groups} />
          <Stat icon={<Sparkles className="h-4 w-4" />} label="Stories" value={stats.stories} />
        </div>

        {/* Settings rows */}
        <ul className="mt-5 space-y-2">
          <SettingsRow
            icon={<Phone className="h-4 w-4 text-white" />}
            iconBg="bg-cc-terracotta"
            label="Phone"
            value={profile?.phone || "Not set"}
            onClick={onEditProfile}
          />
          <SettingsRow
            icon={<Bell className="h-4 w-4 text-white" />}
            iconBg="bg-cc-gold"
            label="Notifications"
            value="Configure alerts"
            onClick={onOpenNotifications}
          />
          <SettingsRow
            icon={<Shield className="h-4 w-4 text-white" />}
            iconBg="bg-cc-forest"
            label="Privacy"
            value="Last seen, photo, status"
            onClick={onOpenPrivacy}
          />
          <li className="chitchat-surface p-3 flex items-center gap-3">
            <span className="h-9 w-9 rounded-lg bg-cc-terracotta flex items-center justify-center shrink-0">
              <Moon className="h-4 w-4 text-white" />
            </span>
            <div className="flex-1">
              <div className="text-sm font-semibold text-cc-ink">Dark mode</div>
              <div className="text-xs text-cc-ink-soft">Easy on the eyes at night</div>
            </div>
            <Switch checked={dark} onCheckedChange={toggleDark} />
          </li>
        </ul>

        <Button
          variant="ghost"
          className="w-full mt-4 text-cc-ink-soft hover:text-cc-terracotta"
          onClick={() => signOut()}
        >
          <LogOut className="h-4 w-4 mr-1.5" /> Sign out
        </Button>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex flex-col items-center text-center">
      <span className="text-cc-terracotta">{icon}</span>
      <div className="font-display text-base font-bold text-cc-ink leading-none mt-1">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-cc-ink-soft mt-0.5">{label}</div>
    </div>
  );
}

function SettingsRow({
  icon, iconBg, label, value, onClick,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string;
  onClick?: () => void;
}) {
  return (
    <li>
      <button
        onClick={onClick}
        className="chitchat-surface w-full p-3 flex items-center gap-3 hover:bg-cc-surface-2 transition text-left"
      >
        <span className={`h-9 w-9 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-cc-ink">{label}</div>
          <div className="text-xs text-cc-ink-soft truncate">{value}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-cc-ink-soft" />
      </button>
    </li>
  );
}
